import type { Pattern } from '@web-stories-wp/patterns';

export type GradientBounds = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};

export function normalizeGradientTurn(value: number) {
  return value >= 0 && value < 1 ? value : ((value % 1) + 1) % 1;
}

export function getGradientBounds(bounds: GradientBounds = {}) {
  return {
    x: Number.isFinite(bounds.x) ? Number(bounds.x) : 0,
    y: Number.isFinite(bounds.y) ? Number(bounds.y) : 0,
    width:
      Number.isFinite(bounds.width) && Number(bounds.width) > 0
        ? Number(bounds.width)
        : 1,
    height:
      Number.isFinite(bounds.height) && Number(bounds.height) > 0
        ? Number(bounds.height)
        : 1,
  };
}

function hasValidGradientStops(stops: NonNullable<Pattern['stops']>) {
  return stops.every((stop) => {
    if (!stop?.color) return false;
    const { position, color } = stop;
    return (
      Number.isFinite(position) &&
      position >= 0 &&
      position <= 1 &&
      color &&
      [color.r, color.g, color.b].every(
        (value) => Number.isFinite(value) && value >= 0 && value <= 255
      ) &&
      (color.a === undefined ||
        (Number.isFinite(color.a) && color.a >= 0 && color.a <= 1))
    );
  });
}

export function isGradientGeometryV2(pattern?: Pattern | null) {
  if (
    !pattern ||
    pattern.geometryVersion !== 2 ||
    !['linear', 'radial'].includes(String(pattern.type)) ||
    !Array.isArray(pattern.stops) ||
    pattern.stops.length < 2
  ) {
    return false;
  }
  const finitePoint = (point?: { x: number; y: number }) =>
    point === undefined ||
    point === null ||
    (Number.isFinite(point.x) && Number.isFinite(point.y));
  return (
    (pattern.rotation === undefined || Number.isFinite(pattern.rotation)) &&
    (pattern.alpha === undefined ||
      (Number.isFinite(pattern.alpha) &&
        pattern.alpha >= 0 &&
        pattern.alpha <= 1)) &&
    finitePoint(pattern.start) &&
    finitePoint(pattern.end) &&
    finitePoint(pattern.center) &&
    (!pattern.size ||
      (Number.isFinite(pattern.size.w) &&
        pattern.size.w > 0 &&
        Number.isFinite(pattern.size.h) &&
        pattern.size.h > 0)) &&
    hasValidGradientStops(pattern.stops)
  );
}

// Stored turns use 0 = down and clockwise rotation, matching the existing picker.
export function getLinearGradientPoints(
  pattern: Pattern,
  bounds: GradientBounds = {}
) {
  const { x, y, width, height } = getGradientBounds(bounds);
  if (pattern.start && pattern.end) {
    return {
      start: {
        x: x + pattern.start.x * width,
        y: y + pattern.start.y * height,
      },
      end: { x: x + pattern.end.x * width, y: y + pattern.end.y * height },
    };
  }
  const radians = (pattern.rotation || 0) * Math.PI * 2;
  const dx = -Math.sin(radians);
  const dy = Math.cos(radians);
  const halfLength = (Math.abs(dx) * width + Math.abs(dy) * height) / 2;
  const cx = x + width / 2;
  const cy = y + height / 2;
  return {
    start: { x: cx - dx * halfLength, y: cy - dy * halfLength },
    end: { x: cx + dx * halfLength, y: cy + dy * halfLength },
  };
}

export function getGradientGeometry(
  pattern: Pattern,
  bounds: GradientBounds = {}
) {
  const { x, y, width, height } = getGradientBounds(bounds);
  if (pattern.type === 'linear') {
    const { start, end } = getLinearGradientPoints(pattern, bounds);
    return {
      gradientUnits: 'userSpaceOnUse' as const,
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
    };
  }
  const cx = x + (pattern.center?.x ?? 0.5) * width;
  const cy = y + (pattern.center?.y ?? 0.5) * height;
  const rx = ((pattern.size?.w ?? 1) * width) / 2;
  const ry = ((pattern.size?.h ?? 1) * height) / 2;
  return {
    gradientUnits: 'userSpaceOnUse' as const,
    cx: 0,
    cy: 0,
    r: 1,
    gradientTransform: `translate(${cx} ${cy}) rotate(${
      (pattern.rotation || 0) * 360
    }) scale(${rx} ${ry})`,
  };
}

// The SVG contains numeric geometry/validated colours only, never authored markup.
export function getGradientSvg(pattern: Pattern, bounds: GradientBounds = {}) {
  const { width, height } = getGradientBounds(bounds);
  const geometry = getGradientGeometry(pattern, { width, height });
  const tag = pattern.type === 'linear' ? 'linearGradient' : 'radialGradient';
  const attributes = Object.entries(geometry)
    .map(([name, value]) => `${name}="${value}"`)
    .join(' ');
  const stops = (pattern.stops || [])
    .map(
      ({ position, color }) =>
        `<stop offset="${position}" stop-color="rgb(${color.r},${color.g},${
          color.b
        })" stop-opacity="${(color.a ?? 1) * (pattern.alpha ?? 1)}"/>`
    )
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none"><defs><${tag} id="gradient" ${attributes}>${stops}</${tag}></defs><rect width="${width}" height="${height}" fill="url(#gradient)"/></svg>`;
}
